import { useDebounce } from '@/hooks/useDebounce';
import { Search } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import DropdownWithRadio from './RadioButtons';

export type ServerFilterField =
  | {
      type: 'search';
      key: string;
      placeholder?: string;
    }
  | {
      type: 'select';
      key: string;
      label?: string;
      options: { label: string; value: string }[];
      defaultValue?: string;
      disabled?: boolean;
    }
  | {
      type: 'date';
      key: string;
      label?: string;
    }
  | {
      type: 'radio-select';
      key: string;
      label: string;
      options: string[];
      defaultValue?: string | null;
      className?: string;
    };

export interface ServerSearchFilterProps {
  fields: ServerFilterField[];
  onChange: (filterState: Record<string, any>) => void;
  noBg?: boolean;
  controlledValues?: Record<string, any>;
}

const ServerSearchFilter = ({
  fields,
  onChange,
  noBg = false,
  controlledValues,
}: ServerSearchFilterProps) => {
  const initialState = fields.reduce((acc, field) => {
    if ('defaultValue' in field && field.defaultValue !== undefined) {
      acc[field.key] = field.defaultValue;
    } else {
      acc[field.key] = '';
    }
    return acc;
  }, {} as Record<string, any>);

  const [filters, setFilters] = useState(initialState);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    if (!controlledValues) return;
    setFilters((prev) => ({ ...prev, ...controlledValues }));
  }, [JSON.stringify(controlledValues)]);

  const isFirstRender = useRef(true);
  const debouncedFilters = useDebounce(filters, 300);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onChange(debouncedFilters);
  }, [debouncedFilters]);

  const updateField = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div
      className={`flex justify-between items-stretch gap-3 flex-wrap rounded-lg
    ${noBg ? 'bg-transparent p-0' : 'bg-[#F2EBEE] p-2'}
  `}
    >
      {fields.map((field) => {
        if (field.type === 'search') {
          return (
            <div
              key={field.key}
              className="flex flex-1 items-center bg-[#F2EBEE] text-[#4A082D] border border-[#E1D4DB] rounded-lg px-3"
            >
              <Search className="text-[#4A082D] h-5 w-5" />
              <input
                type="text"
                placeholder={field.placeholder || 'Search items...'}
                className="px-2 w-full py-3 bg-transparent focus:outline-none text-sm placeholder:text-[#5c1f42]"
                value={filters[field.key]}
                onChange={(e) => updateField(field.key, e.target.value)}
              />
            </div>
          );
        }
        if (field.type === 'select') {
          return (
            <select
              key={field.key}
              disabled={field.disabled || false}
              value={filters[field.key]}
              onChange={(e) => updateField(field.key, e.target.value)}
              className="px-2 py-3 border bg-[#F2EBEE] border-[#4A082D1A] rounded-md text-sm text-[#4A082D]"
            >
              {field.options.map((opt) => (
                <option value={opt.value} key={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          );
        }

        if (field.type === 'date') {
          return (
            <input
              key={field.key}
              type="date"
              value={filters[field.key]}
              onChange={(e) => updateField(field.key, e.target.value)}
              className="px-3 py-2 border bg-[#F2EBEE] border-[#4A082D1A] rounded-md text-sm text-[#4A082D]"
            />
          );
        }

        if (field.type === 'radio-select') {
          const getAllOption = (label: string) => {
            const lowerLabel = label.toLowerCase();
            if (lowerLabel.includes('status')) return 'All Status';
            if (lowerLabel.includes('select')) return 'Show All';
            if (lowerLabel.includes('language')) return 'All Language';
            if (lowerLabel.includes('level')) return 'All Level';
            if (lowerLabel.includes('type')) return 'All Type';
            if (lowerLabel.includes('role')) return 'All Role';
            if (lowerLabel.includes('limitsites')) return '25';
            if (lowerLabel.includes('limit')) return '25';
            if (lowerLabel.includes('filter')) return 'filterBy';
            else return `All ${label}`;
          };

          const allOption = getAllOption(field.label);

          return (
            <Fragment key={field.key}>
              {(field.key.includes('limit') || field.label.includes('limit')) && (
                <p className="pt-2 text-[#4A082D]">Show</p>
              )}
              <DropdownWithRadio
                key={field.key}
                label={field.label}
                options={
                  field.key.includes('limit') ? field.options : [allOption, ...field.options]
                }
                selected={filters[field.key]}
                onChange={(value) => updateField(field.key, value)}
                isOpen={openDropdown === field.key}
                onOpen={() => setOpenDropdown(field.key)}
                onClose={() => setOpenDropdown((prev) => (prev === field.key ? null : prev))}
                placeholder={allOption}
                className={field.className ?? ''}
              />
              {field.key === 'limit' && <p className="pt-2">Projects</p>}
              {field.label.includes('limitSites') && <p className="pt-2">Sites</p>}
            </Fragment>
          );
        }

        return null;
      })}
    </div>
  );
};

export default ServerSearchFilter;
